import * as React from "react";
import { useTranslation } from "react-i18next";

import { ButtonContainer } from "components/button-container/ButtonContainer";
import Button from "components/button/Button";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { AutopilotDeviceIdentifier, autopilotService } from "services/report/AutopilotService";

import testIds from "testIds.json";

const AutopilotRecheck = (props: {
    devices: AutopilotDeviceIdentifier[];
    showModal: boolean;
    onClose: (done: number) => void;
}): JSX.Element => {
    const { t } = useTranslation();

    const [loading, setLoading] = React.useState(false);
    const [recheckError, setRecheckError] = React.useState(false);
    const [consumedAmount, setConsumedAmount] = React.useState(0);
    const [failedDevicesCount, setFailedDevicesCount] = React.useState(0);
    const [modalVisible, setModalVisible] = React.useState(props.showModal);

    async function recheckAutopilot() {
        setLoading(true);
        await autopilotService
            .recheck(props.devices, false)
            .then((response) => {
                setLoading(false);
                setConsumedAmount(response.consumedLicenses.reduce((total, license) => total + license.amount, 0));
                setFailedDevicesCount(
                    response.consumedLicenses.reduce(
                        (total, license) => total + (license.failed_devices?.length || 0),
                        0
                    )
                );
            })
            .catch(() => {
                setLoading(false);
                setRecheckError(true);
            });
    }

    React.useEffect(() => {
        setModalVisible(props.showModal);
        if (props.showModal) {
            recheckAutopilot();
        }
    }, [props.showModal]);

    const hideModal = () => {
        setModalVisible(false);
        setConsumedAmount(0);
        setFailedDevicesCount(0);
        props.onClose(consumedAmount);
    };

    return (
        <>
            <Modal isOpen={modalVisible} modalTitle={t("Autopilot.recheckModal.title")} hideModal={hideModal}>
                {loading ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <TextBlock>
                            {recheckError
                                ? t("Autopilot.recheckModal.errorMessage")
                                : t("Autopilot.recheckModal.consumptionInformationComplete")}
                        </TextBlock>

                        {failedDevicesCount > 0 && (
                            <StatusBadge
                                values={[
                                    {
                                        status: Status.WARNING,
                                        title: t("Autopilot.recheckModal.partialFailure", {
                                            amount: failedDevicesCount,
                                        }),
                                    },
                                ]}
                            />
                        )}

                        <ButtonContainer>
                            <Button variant="PRIMARY" onClick={hideModal} testId={testIds.common.dialog.closeButton}>
                                {t("Common.close")}
                            </Button>
                        </ButtonContainer>
                    </>
                )}
            </Modal>
        </>
    );
};

export default AutopilotRecheck;
